/* istanbul ignore file  */
import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { Box, Snackbar, Skeleton } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { useParticipantById } from "@/hooks/useParticipants";
import { usePageStore } from "@/hooks/usePageStore";
import { useAuditLogFirstRender } from "@/hooks/useAuditLogs";
import { ACTION_CODE, MODULE_NAME, OPTIONS } from "@/domain/auditlog.enum";
import { useUserStore } from "@/hooks/useUserStore";
import ProfileBreadcrumb from "@/ui/breadcrumbs/clients/ProfileBreadcrumb";
import ProfileCards from "@/ui/content/clients/ProfileCards";
import { ParticipantResponse } from "@/domain/participant.interface";
import AlertDialog from "@/components/AlertDialog";
import { PermissionDeniedModal } from "@/components/PermissionDeniedModal/PermissionDeniedModal";
import { useListEnrollments } from "@/hooks/useEnrollments";
import EnrollmentList from "@/ui/content/clients/EnrollmentList";
import EnrollProgramModal from "@/ui/modals/clients/EnrollProgramModal";
import AuditLogModal from "@/ui/modals/AuditLogModal";
import { AlertType, useAlertModal } from "@/hooks/useAlertModal";
import TestResultValidation from "@/ui/modals/participants/TestResultValidation";
import TestResultImagesModal from "@/ui/modals/participants/TestResultImagesModal";
import ParticipantUpdateModal from "@/ui/modals/participants/ParticipantUpdateModal";
import { useCountrySetting } from "@/hooks/useSetting";
import { EnrollmentStatus, Enrollment } from "@/domain/enrollment.interface";
import { useSoftDeleteParticipant } from "@/hooks/useParticipants";
import { useFeedBack } from "@/providers/FeedBackProvider/FeedBackContext";
import { useNavigate } from "react-router-dom";
import OnDemandIncentiveModal from "@/ui/modals/clients/OnDemandIncentiveModal";
import TransactionHistoryModal from "@/ui/modals/clients/TransactionHistoryModal";
import { ErrorResponse } from "@/domain/error.interface";

const ParticipantProfile: React.FC = () => {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const { userInfo, modules, fetchModules, userHasAccessToCode } =
    useUserStore();
  const { showSnackBar } = useFeedBack();
  const { participantId } = useParams();
  const [isSnackbarOpen, setIsSnackbarOpen] = useState(false);
  const [snackbarMessage] = useState("");
  const { setPageTitle } = usePageStore();

  const [openAuditLogModal, setOpenAuditLogModal] = useState(false);
  const [showUpdateModal, setShowUpdateModal] = useState(false);
  const [isOnDemandIncentiveModalOpen, setIsOnDemandIncentiveModalOpen] =
    useState(false);
  const [selectedEnrollmentForIncentive, setSelectedEnrollmentForIncentive] =
    useState<Enrollment | null>(null);
  const [isTransactionHistoryModalOpen, setIsTransactionHistoryModalOpen] =
    useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showPermissionDeniedModal, setShowPermissionDeniedModal] =
    useState(false);

  const { alertState, showError, closeAlert, confirmAction } = useAlertModal();
  const countrySetting = useCountrySetting();
  const country = useMemo(() => countrySetting, [countrySetting]);
  const softDeleteParticipant = useSoftDeleteParticipant();

  const {
    data: participantResponse,
    isLoadingParticipant,
    isErrorParticipant,
    refetchParticipant,
  } = useParticipantById(participantId || "");

  const { enrollments, isLoadingEnrollments, refetchEnrollments } =
    useListEnrollments(participantId || "", userInfo?._id || "", true);

  const [isEnrollProgramModalOpen, setIsEnrollProgramModalOpen] =
    useState(false);

  const [isTestResultModalOpen, setIsTestResultModalOpen] = useState(false);
  const [isTestResultImagesModalOpen, setIsTestResultImagesModalOpen] =
    useState(false);
  const [selectedEnrollmentId, setSelectedEnrollmentId] = useState<string>("");
  const [selectedTestResultId, setSelectedTestResultId] = useState<string>("");
  const [selectedValidationType, setSelectedValidationType] =
    useState<string>("");
  const [selectedEnrollmentStatus, setSelectedEnrollmentStatus] =
    useState<EnrollmentStatus | null>(null);
  const [refetchTestResultsFn, setRefetchTestResultsFn] = useState<
    (() => void) | null
  >(null);

  const handleOpenEditModal = () => {
    if (userHasAccessToCode("PARTICIPANT_EDIT")) {
      setShowUpdateModal(true);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleOpenEnrollProgramModal = () => {
    // Open modal immediately, validation will happen inside the modal
    setIsEnrollProgramModalOpen(true);
    // Refetch participant data in the background
    refetchParticipant();
  };

  const handleCloseEnrollProgramModal = () => {
    setIsEnrollProgramModalOpen(false);
    refetchParticipant();
    refetchEnrollments();
  };

  const handleSnackbarClose = () => setIsSnackbarOpen(false);

  // AC00-A: Logging User-Related Actions
  useAuditLogFirstRender({
    appType: "WEB_BACK_OFFICE",
    module: MODULE_NAME.PARTICIPANTS,
    option: OPTIONS.PARTICIPANT_PROFILE,
    actionCode: ACTION_CODE.WEB_PART_PROFILE_VIEW,
    action: "When entering",
    detail: participantResponse?.participant
      ? `Viewed participant ${participantResponse.participant.firstName} ${participantResponse.participant.lastName} profile`
      : "",
    transactionDate: new Date(),
    accountId: `${process.env.REACT_APP_ACCOUNT_ID}`,
    createdBy: userInfo?._id as string,
    targetId: participantResponse?.participant._id as string,
    name: `${userInfo?.firstName} ${userInfo?.lastName}`,
  });

  // AC02: Display Participant Name in page title
  useEffect(() => {
    if (
      participantResponse?.participant?.firstName &&
      participantResponse?.participant?.lastName
    ) {
      const fullName = `${participantResponse.participant.firstName} ${participantResponse.participant.lastName}`;
      setPageTitle(fullName);
    }
  }, [participantResponse, setPageTitle]);

  useEffect(() => {
    if (!modules) {
      fetchModules();
    }
  }, [modules, fetchModules]);

  // Check if participant is deleted and redirect to 404
  useEffect(() => {
    if (participantResponse?.participant?.isDeleted) {
      navigate("/404");
    }
  }, [participantResponse, navigate]);

  const participantName = useMemo(() => {
    if (!participantResponse?.participant) return "";

    return `${participantResponse?.participant.firstName} ${participantResponse?.participant.lastName}`;
  }, [participantResponse]);

  const handleViewImages = (
    testResultId: string,
    enrollmentId: string,
    enrollmentStatus: EnrollmentStatus,
    validationType: string,
  ) => {
    // COYN-1095: Check permission before opening modal
    if (userHasAccessToCode("PARTICIPANT_VIEW_IMAGE_RESULTS")) {
      setSelectedTestResultId(testResultId);
      setSelectedEnrollmentId(enrollmentId);
      setSelectedEnrollmentStatus(enrollmentStatus);
      setSelectedValidationType(validationType);
      // AC01: Open images modal instead of video modal
      setIsTestResultImagesModalOpen(true);
    } else {
      // Show permission denied modal if user doesn't have access
      setShowPermissionDeniedModal(true);
    }
  };

  const handleRefetchTestResults = (refetchFn: () => void) => {
    setRefetchTestResultsFn(() => refetchFn);
  };

  const handleCloseTestResultModal = () => {
    queryClient.invalidateQueries({
      queryKey: ["coyn-test-results", selectedEnrollmentId],
    });

    setIsTestResultModalOpen(false);
    setSelectedTestResultId("");
    setSelectedEnrollmentId("");
    refetchParticipant();
    refetchEnrollments();
    if (refetchTestResultsFn) {
      refetchTestResultsFn();
    }
  };

  // AC04: Close images modal
  const handleCloseTestResultImagesModal = () => {
    setIsTestResultImagesModalOpen(false);
    setSelectedTestResultId("");
    setSelectedEnrollmentId("");
    setSelectedValidationType("");
  };

  const handleMaxReached = () => {
    showError(
      "Sorry!",
      "The participant reached the maximum yearly earnings configured for your account. The enrolled program will be automatically completed. The participant will be able to join a new program on January 1st of next year.",
    );
  };

  const handleOnDemandIncentive = (enrollment: Enrollment) => {
    if (!enrollment) {
      showError("Error", "No enrollment found for this participant.");
      return;
    }
    // Store the enrollment for the modal
    setSelectedEnrollmentForIncentive(enrollment);
    setIsOnDemandIncentiveModalOpen(true);
  };

  const handleCloseOnDemandIncentiveModal = () => {
    setIsOnDemandIncentiveModalOpen(false);
    setSelectedEnrollmentForIncentive(null);
    refetchParticipant();
    refetchEnrollments();
    if (refetchTestResultsFn) {
      refetchTestResultsFn();
    }
  };

  const handleDelete = () => {
    if (userHasAccessToCode("PARTICIPANT_DELETE_CLIENT")) {
      setShowDeleteModal(true);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleOpenTransactionHistory = () => {
    if (userHasAccessToCode("PARTICIPANT_VIEW_REWARD_TRXS")) {
      setIsTransactionHistoryModalOpen(true);
    } else {
      setShowPermissionDeniedModal(true);
    }
  };

  const handleCloseTransactionHistory = () => {
    setIsTransactionHistoryModalOpen(false);
  };

  const handleConfirmDelete = async () => {
    if (!participantId) return;
    try {
      await softDeleteParticipant.mutateAsync(participantId, {
        onSuccess: () => {
          showSnackBar("The registry has been deleted", "success");
          navigate("/participants");
        },
        onError: (error: ErrorResponse) => {
          const message = Array.isArray(error.message)
            ? error.message[0]
            : error.message;
          showSnackBar(message || "Failed to delete participant", "error");
        },
      });
    } catch {
      showSnackBar("Failed to delete participant", "error");
    }
    setShowDeleteModal(false);
  };

  return (
    <Box sx={{ minWidth: 0, overflowX: "hidden" }}>
      {/* AC00-D: Breadcrumb skeleton while loading */}
      {isLoadingParticipant ? (
        <Skeleton variant="text" width={300} height={24} sx={{ mb: 2 }} />
      ) : (
        <ProfileBreadcrumb participantName={participantName} />
      )}

      <ProfileCards
        participantResponse={participantResponse || ({} as ParticipantResponse)}
        isLoadingParticipant={isLoadingParticipant}
        isErrorParticipant={isErrorParticipant}
        handleEditParticipant={handleOpenEditModal}
        handleAuditLogs={() => setOpenAuditLogModal(true)}
        handleDelete={handleDelete}
        handleTransactionHistory={handleOpenTransactionHistory}
      />

      <EnrollmentList
        enrollments={enrollments}
        isLoadingEnrollments={isLoadingEnrollments}
        refetchEnrollments={refetchEnrollments}
        refetchParticipant={refetchParticipant}
        participantName={participantName}
        handleViewImages={handleViewImages}
        onRefetchTestResults={handleRefetchTestResults}
        handleEnrollToProgram={handleOpenEnrollProgramModal}
        handleOnDemandIncentive={handleOnDemandIncentive}
      />

      {isEnrollProgramModalOpen ? (
        <EnrollProgramModal
          open={isEnrollProgramModalOpen}
          onClose={handleCloseEnrollProgramModal}
          participantResponse={participantResponse}
        />
      ) : null}

      {participantResponse?.participant && showUpdateModal ? (
        <ParticipantUpdateModal
          open={showUpdateModal}
          participant={participantResponse?.participant}
          onClose={() => {
            setShowUpdateModal(false);
            refetchParticipant();
          }}
          country={country}
        />
      ) : null}

      {isTestResultModalOpen && selectedTestResultId && (
        <TestResultValidation
          open={isTestResultModalOpen}
          onClose={handleCloseTestResultModal}
          enrollmentId={selectedEnrollmentId}
          testResultId={selectedTestResultId}
          onMaxReached={handleMaxReached}
          programName={enrollments?.[0]?.program?.name || ""}
          participantName={participantName}
          enrollmentStatus={selectedEnrollmentStatus as EnrollmentStatus}
        />
      )}

      {/* AC01: Validation Images Modal */}
      {isTestResultImagesModalOpen && selectedTestResultId && (
        <TestResultImagesModal
          open={isTestResultImagesModalOpen}
          onClose={handleCloseTestResultImagesModal}
          enrollmentId={selectedEnrollmentId}
          testResultId={selectedTestResultId}
          validationType={selectedValidationType}
        />
      )}

      {alertState.open && (
        <AlertDialog
          open={alertState.open}
          onCancel={
            alertState.alertType === AlertType.CONFIRMATION
              ? closeAlert
              : closeAlert
          }
          onConfirm={confirmAction}
          title={alertState.title}
          description={alertState.message}
          confirmText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Confirm"
              : "Accept"
          }
          cancelText={
            alertState.alertType === AlertType.CONFIRMATION
              ? "Cancel"
              : undefined
          }
        />
      )}

      {openAuditLogModal ? (
        <AuditLogModal
          open={openAuditLogModal}
          module={MODULE_NAME.ENROLLMENTS}
          onClose={() => setOpenAuditLogModal(false)}
        />
      ) : null}

      {isOnDemandIncentiveModalOpen && selectedEnrollmentForIncentive ? (
        <OnDemandIncentiveModal
          open={isOnDemandIncentiveModalOpen}
          onClose={handleCloseOnDemandIncentiveModal}
          participantName={participantName}
          enrollment={selectedEnrollmentForIncentive}
        />
      ) : null}

      {isTransactionHistoryModalOpen && participantId && userInfo?._id ? (
        <TransactionHistoryModal
          open={isTransactionHistoryModalOpen}
          onClose={handleCloseTransactionHistory}
          participantId={participantId}
          userId={userInfo._id}
        />
      ) : null}

      {showDeleteModal && (
        <AlertDialog
          open={showDeleteModal}
          title="Delete"
          description="Are you you want to delete this registry?"
          onCancel={() => setShowDeleteModal(false)}
          onConfirm={handleConfirmDelete}
          confirmText="Delete"
          cancelText="Cancel"
        />
      )}

      <Snackbar
        open={isSnackbarOpen}
        autoHideDuration={3000}
        onClose={handleSnackbarClose}
        message={snackbarMessage}
      />

      <PermissionDeniedModal
        open={showPermissionDeniedModal}
        onClose={() => setShowPermissionDeniedModal(false)}
      />
    </Box>
  );
};

export default ParticipantProfile;
